import React, { useContext, useState } from "react";
import { toast } from "react-toastify";
import {
  useCalculateNegotiationRates,
  useSetupNegotiationAgent,
} from "../../../../services/negotiation/negotiationService";
import classes from "../../TrucksAvailable.module.css";
import { useMixPanelForTracking } from "../../../../utils/TrackMixPanel";
import { UserApiContext } from "../../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../../MyCompanyProfileContext";

const dedupeLocationName = (name) => {
  if (!name) return name;
  const parts = name.split(",").map((p) => p.trim());
  const seen = [];
  parts.forEach((p) => {
    if (p && !seen.includes(p)) seen.push(p);
  });
  return seen.join(", ");
};

const formatCurrency = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `₹${num.toLocaleString("en-IN")}`;
};

const LaneRow = ({ item, targetRate, rounds, onTargetRateChange, onRoundsChange, onDataLoaded }) => {
  const truckEnquiryId = item?.truck_enquiry_id;
  const { data, isPending, isError, error, refetch } =
    useCalculateNegotiationRates(truckEnquiryId);

  const isCalculated = data?.status === "calculated" || data?.status === "cached";
  const showNoHistory =
    data?.has_history === false || data?.status === "awaiting_manual_input";

  const seededRef = React.useRef(false);
  React.useEffect(() => {
    if (!seededRef.current && isCalculated) {
      seededRef.current = true;
      if (data?.has_history !== false) {
        onTargetRateChange(data?.target_rate ?? "");
      }
      onDataLoaded(data);
    }
  }, [data]);

  const origin = dedupeLocationName(item?.lane_details?.origin?.location_name);
  const destination = dedupeLocationName(item?.lane_details?.destination?.location_name);
  const weight = item?.lane_details?.weight?.value || item?.lane_details?.weight;
  const truckType = Array.isArray(item?.lane_details?.truck_types)
    ? item?.lane_details?.truck_types.map((t) => t?.label).join(", ")
    : item?.lane_details?.truck_types?.label;
  const currentL1 = item?.lane_details?.current_l1_rate || item?.current_l1_rate;

  const httpStatus = isError && error?.response?.status;

  return (
    <div
      className={classes["lorri-lane-row"]}
    >
      <div className={classes["lorri-lane-info"]}>
        <div className={classes["lorri-lane-route"]}>
          {origin} → {destination}
        </div>
        <div className={classes["lorri-lane-meta"]}>
          {truckType || ""}
          {weight ? ` (${weight})` : ""}
          {showNoHistory
            ? " · new lane · no past Spot data"
            : currentL1
              ? ` · continues from current L1 ${formatCurrency(currentL1)}`
              : ""}
        </div>
      </div>

      <div className={classes["lorri-lane-target"]}>
        <div className={classes["lorri-lane-label"]}>
          {showNoHistory ? "SET TARGET RATE" : "PROPOSED TARGET"}
        </div>
        {isPending ? (
          <div
            className={classes["skeleton-bar"]}
            style={{ width: "140px", height: "30px", marginTop: "4px" }}
          />
        ) : (
          <input
            type="text"
            inputMode="numeric"
            placeholder={showNoHistory ? "₹—" : ""}
            value={formatCurrency(targetRate)}
            onChange={(e) => onTargetRateChange(e.target.value.replace(/[^\d]/g, ""))}
            className={showNoHistory ? classes["lorri-target-input-empty"] : ""}
            style={{
              border: showNoHistory ? "1px dashed #8a93a0" : "1px solid #cdd3de",
              borderRadius: "6px",
              padding: "6px 10px",
              width: "140px",
              fontWeight: 600,
              color: "#393185",
              marginTop: "4px",
            }}
          />
        )}
      </div>

      <div className={classes["lorri-lane-rounds"]}>
        <div className={classes["lorri-lane-label"]}>
          ROUNDS
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
          <button
            type="button"
            className="btn btn-sm"
            style={{ border: "1px solid #cdd3de" }}
            onClick={() => onRoundsChange(Math.max(1, rounds - 1))}
          >
            −
          </button>
          <span style={{ fontWeight: 700 }}>{rounds}</span>
          <button
            type="button"
            className="btn btn-sm"
            style={{ border: "1px solid #cdd3de" }}
            onClick={() => onRoundsChange(rounds + 1)}
          >
            +
          </button>
        </div>
      </div>

      <div className={classes["lorri-lane-status"]}>
        {isPending ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "13px" }}>
            <i className="fa fa-spinner fa-spin" />
            Fetching…
          </div>
        ) : showNoHistory ? (
          <>
            <div className={classes["lorri-badge-info"]}>
              <i className="fa fa-info-circle" /> No historical rates
            </div>
            <div className={classes["lorri-lane-reasoning"]}>
              This lane has no past Spot data to price from. <b>Please enter the target rate</b> you want the agent to work toward.
            </div>
          </>
        ) : isCalculated ? (
          <>
            <div className={classes["lorri-badge-success"]}>
              ✓ Agent's recommendation
            </div>
            <div className={classes["lorri-lane-reasoning"]}>
              Estimated from your historical Spot rates and market intelligence. You can change it to set your own.
            </div>
          </>
        ) : data?.status === "failed" ? (
          <div style={{ color: "#dc2626", fontSize: "13px" }}>
            {data?.message || "Failed to calculate suggestion. You can enter values manually."}
          </div>
        ) : data?.status === "disabled" ? (
          <div style={{ color: "#b45309", fontSize: "13px" }}>
            {data?.reason || "Agent is currently disabled."}
          </div>
        ) : isError ? (
          <div style={{ color: "#dc2626", fontSize: "13px" }}>
            {httpStatus === 400
              ? "Enquiry couldn't be resolved."
              : "Something went wrong, please retry."}
            <button
              type="button"
              className="btn btn-sm btn-link"
              style={{ padding: "0 0 0 6px" }}
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const BulkAssignLorriAgentModal = ({
  items,
  onClose,
  company_id,
  fetchSpotData,
  currentFilterPayload,
  defaultRounds,
}) => {
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);
  const [laneState, setLaneState] = useState(() =>
    Object.fromEntries(
      items.map((item) => [
        item.truck_enquiry_id,
        { targetRate: "", rounds: defaultRounds ?? 3, walkawayRate: 0 },
      ])
    )
  );

  const { mutate: confirmNegotiation, isPending: isConfirming } =
    useSetupNegotiationAgent();

  const setTargetRate = (id, value) =>
    setLaneState((prev) => ({ ...prev, [id]: { ...prev[id], targetRate: value } }));

  const setRounds = (id, value) =>
    setLaneState((prev) => ({ ...prev, [id]: { ...prev[id], rounds: value } }));

  const setWalkawayRate = (id, data) =>
    setLaneState((prev) => ({
      ...prev,
      [id]: { ...prev[id], walkawayRate: Number(data?.walkaway_rate) || 0 },
    }));

  const handleConfirmNegotiation = () => {
    const missingTarget = items.some((item) => {
      const lane = laneState[item.truck_enquiry_id] || {};
      return !Number(lane.targetRate);
    });
    if (missingTarget) {
      toast.error("Please enter a target rate for every lane before confirming.");
      return;
    }

    const spotNegotiationList = items.map((item) => {
      const id = item.truck_enquiry_id;
      const lane = laneState[id] || {};
      return {
        truck_enquiry_id: id,
        agent_enabled: true,
        max_rounds_per_transporter: lane.rounds ?? 3,
        target_rate: Number(lane.targetRate) || 0,
        walkaway_rate: lane.walkawayRate ?? 0,
        company_id,
      };
    });

    confirmNegotiation(spotNegotiationList, {
      onSuccess: (res) => {
        const results = Array.isArray(res) ? res : res?.results;
        const failed = Array.isArray(results)
          ? results.filter((r) => r?.status !== "ok")
          : [];

        if (Array.isArray(results)) {
          if (failed.length === 0) {
            toast.success(
              `Negotiation started successfully for ${results.length} lane${results.length === 1 ? "" : "s"}`
            );
            TrackMixPanel({
              ModuleName: "lorri spot",
              ScreenName: "my enquiries - bulk assign lorri agent",
              Data: `no of lanes: ${results.length}`,
              Definition: `user bulk assigned LoRRI Agent to enquiries`,
            });
            if (fetchSpotData && currentFilterPayload) {
              fetchSpotData(currentFilterPayload);
            }
            onClose && onClose();
          } else if (failed.length < results.length) {
            toast.error(
              `${failed.length} of ${results.length} lanes couldn't be started — please retry those.`
            );
            if (fetchSpotData && currentFilterPayload) {
              fetchSpotData(currentFilterPayload);
            }
          } else {
            toast.error(failed[0]?.error || "Something went wrong");
          }
        } else if (res?.status) {
          toast.success(res?.messages || "Negotiation started successfully");
          TrackMixPanel({
            ModuleName: "lorri spot",
            ScreenName: "my enquiries - bulk assign lorri agent",
            Data: `no of lanes: ${items.length}`,
            Definition: `user bulk assigned LoRRI Agent to enquiries`,
          });
          if (fetchSpotData && currentFilterPayload) {
            fetchSpotData(currentFilterPayload);
          }
          onClose && onClose();
        } else {
          toast.error(res?.message || "Something went wrong");
        }
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Something went wrong");
      },
    });
  };

  return (
    <div style={{ padding: "8px 4px" }}>
      {items.map((item) => (
        <LaneRow
          key={item.truck_enquiry_id}
          item={item}
          targetRate={laneState[item.truck_enquiry_id]?.targetRate ?? ""}
          rounds={laneState[item.truck_enquiry_id]?.rounds ?? 3}
          onTargetRateChange={(value) => setTargetRate(item.truck_enquiry_id, value)}
          onRoundsChange={(value) => setRounds(item.truck_enquiry_id, value)}
          onDataLoaded={(data) => setWalkawayRate(item.truck_enquiry_id, data)}
        />
      ))}

      <div
        style={{
          marginTop: "16px",
          background: "#f3f0fb",
          borderRadius: "8px",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <span style={{ fontWeight: 700, color: "#393185" }}>
            {items.length} lane{items.length === 1 ? "" : "s"} ready
          </span>
          <span style={{ color: "#6b7280", fontSize: "13px", marginLeft: "8px" }}>
            Set the target and rounds for each lane above.
          </span>
        </div>
        <button
          type="button"
          className="btn"
          disabled={isConfirming}
          style={{
            background: "#16a34a",
            color: "#fff",
            fontWeight: 600,
            border: "none",
            padding: "8px 16px",
            borderRadius: "6px",
            opacity: isConfirming ? 0.7 : 1,
          }}
          onClick={handleConfirmNegotiation}
        >
          {isConfirming ? "Starting negotiation…" : "✓ Confirm & start negotiation"}
        </button>
      </div>
    </div>
  );
};

export default BulkAssignLorriAgentModal;
